// `/admin/admins` (croqui `admin/administradores.tsx`). Foco: **a lista**. Criar e remover
// são `ADMIN_STEP_UP_ROUTES`: o `StepUpDialog` sobe de `api/client.ts`.
//
// O croqui não desenha confirmação para "Remover", mas ela fica: a janela do step-up é de
// 5 min, então logo depois de criar um admin o clique em "Remover" de outra linha sairia
// sem confirmação nenhuma, e a remoção é irreversível (mesmo argumento do item 7 das
// "Questões abertas" de WEB-73). Registrado em "Questões abertas".

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { errorMessageOf } from '../api/errors.ts';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { type AdminRowError, AdminsPanel } from '../features/admin-audit/admins-panel.tsx';
import { isStepUpCancelled } from '../features/admin-audit/model.ts';
import { NewAdminDrawer } from '../features/admin-audit/new-admin-drawer.tsx';
import {
  type AdminUser,
  adminAuditKeys,
  adminMeQueryOptions,
  adminsQueryOptions,
  type CreatedAdmin,
  createAdminUser,
  removeAdminUser,
} from '../features/admin-audit/queries.ts';

function Admins() {
  const queryClient = useQueryClient();
  const admins = useQuery(adminsQueryOptions());
  const me = useQuery(adminMeQueryOptions());

  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedAdmin | undefined>(undefined);
  const [toRemove, setToRemove] = useState<AdminUser | undefined>(undefined);
  const [rowError, setRowError] = useState<AdminRowError | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: adminAuditKeys.admins });
  };

  const create = useMutation({
    mutationFn: createAdminUser,
    onSuccess: (admin) => {
      // O drawer troca o formulário pelo aviso de sucesso (croqui `NovoAdmin`, 2º estado).
      setCreated(admin);
      void refresh();
    },
    onError: (error) => {
      if (isStepUpCancelled(error)) {
        setCreateOpen(false);
      }
    },
  });

  const remove = useMutation({
    mutationFn: async (admin: AdminUser) => {
      setRowError(undefined);
      return removeAdminUser(admin.id);
    },
    onSuccess: () => {
      setToRemove(undefined);
    },
    onError: (error, admin) => {
      setToRemove(undefined);
      // O `409 invalid_state` do último admin vem do servidor: a mensagem é dele.
      if (!isStepUpCancelled(error)) {
        setRowError({ adminId: admin.id, message: errorMessageOf(error) });
      }
    },
    onSettled: refresh,
  });

  const openCreate = (): void => {
    create.reset();
    setCreated(undefined);
    setCreateOpen(true);
  };

  const rows = admins.data ?? [];

  return (
    <>
      <PageActions>
        <Btn kind="primary" icon="lock" onClick={openCreate}>
          Novo administrador
        </Btn>
      </PageActions>

      <Panel className="min-w-0 flex-1">
        <AdminsPanel
          admins={rows}
          currentAdminId={me.data?.id}
          isLoading={admins.isPending}
          error={admins.data === undefined ? admins.error : undefined}
          onRetry={() => {
            void admins.refetch();
          }}
          onRemove={setToRemove}
          onCreate={openCreate}
          {...(rowError === undefined ? {} : { rowError })}
        />
      </Panel>

      <NewAdminDrawer
        open={createOpen}
        isPending={create.isPending}
        error={create.error === null || isStepUpCancelled(create.error) ? undefined : create.error}
        created={created}
        onSubmit={(input) => {
          create.mutate(input);
        }}
        onOpenChange={setCreateOpen}
      />

      <ConfirmDialog
        open={toRemove !== undefined}
        title="Remover este administrador?"
        description={
          toRemove === undefined
            ? ''
            : `${toRemove.name} (${toRemove.email}) perde o acesso ao backoffice imediatamente.`
        }
        confirmLabel="Remover"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          if (toRemove !== undefined) {
            remove.mutate(toRemove);
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setToRemove(undefined);
          }
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/admins/')({ component: Admins });
